/**
 * Module-level cache for the quote T&C template catalog.
 *
 * Same shape as ``useAppSettings``: one shared fetch across the app,
 * invalidation notifies live consumers, synchronous accessor for
 * non-React readers. Templates change rarely (admin-driven) and the
 * payload is tiny — a shared module cache is the right size.
 */

import { useEffect, useState } from 'react';
import type { QuoteTermsTemplate } from '@techgitter/contracts';

import { fetchQuoteTermsTemplates } from '../services/quoteTermsTemplatesApi';

let cache: QuoteTermsTemplate[] | null = null;
let inflight: Promise<QuoteTermsTemplate[]> | null = null;
const subscribers = new Set<() => void>();

const getCatalog = (): Promise<QuoteTermsTemplate[]> => {
  if (cache !== null) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetchQuoteTermsTemplates()
      .then((items) => {
        cache = items;
        return items;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
};

/** Discard the cached catalog and notify live consumers. Call after
 *  any successful create / update / delete / set-default. */
export function invalidateQuoteTermsTemplatesCache(): void {
  cache = null;
  inflight = null;
  subscribers.forEach((notify) => {
    try {
      notify();
    } catch {
      /* keep other subscribers running on a single failure */
    }
  });
}

/** Synchronous read. Returns ``null`` until the first fetch resolves. */
export function getCachedQuoteTermsTemplates(): QuoteTermsTemplate[] | null {
  return cache;
}

/** Convenience: the currently-default template (or null when the
 *  catalog hasn't loaded / is empty / no row carries the flag). */
export function getDefaultQuoteTermsTemplate(): QuoteTermsTemplate | null {
  if (!cache) return null;
  return cache.find((t) => t.isDefault) ?? null;
}

export interface UseQuoteTermsTemplatesResult {
  templates: QuoteTermsTemplate[];
  isLoading: boolean;
  error: string | null;
}

export function useQuoteTermsTemplates(): UseQuoteTermsTemplatesResult {
  const [templates, setTemplates] = useState<QuoteTermsTemplate[]>(cache ?? []);
  const [isLoading, setIsLoading] = useState(cache === null);
  const [error, setError] = useState<string | null>(null);
  const [rev, setRev] = useState(0);

  useEffect(() => {
    const onInvalidate = () => setRev((r) => r + 1);
    subscribers.add(onInvalidate);
    return () => {
      subscribers.delete(onInvalidate);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(cache === null);
    getCatalog()
      .then((items) => {
        if (cancelled) return;
        setTemplates(items);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rev]);

  return { templates, isLoading, error };
}
